import { LoggingService } from '@instinet-analytics/core-lib';
import { Store } from 'redux';
import { AppMode } from 'app/store/general/AppMode';
import { selectAppMode } from 'app/store/general/selectors';
import { InMessageError } from './InMessageError';
import {
    AjvValidator,
    ValidationResult,
} from 'utils/jsonValidation/AjvValidator';
import { JSONSchemaType } from 'ajv';

export interface InMessageRequirements {
    supportedAppModes: AppMode[];
    mustBeAuthenticated: boolean;
}

export interface InMessageTopicContract<TPayload> {
    requirements: InMessageRequirements;
    pendingAuthentication?: {
        payload: TPayload;
        uid: string;
        timeStamp: number;
    };
    assertOkToAcceptIncoming: (
        uid: string,
        payload: unknown,
        store: Store
    ) => InMessageError | undefined;
    handle: (
        uid: string,
        payload: TPayload,
        store: Store,
        loggingService: LoggingService
    ) => void;
    validate: (payload: unknown) => ValidationResult;
    savePendingAuthentication: (payload: TPayload, uid: string) => void;
}

export interface SanitisesPayload<TPayload> {
    sanitizePayload: (payload: TPayload) => unknown;
}

export function isSanitisesPayload<TPayload>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filter: any
): filter is SanitisesPayload<TPayload> {
    return 'sanitizePayload' in filter;
}

export abstract class InMessageTopic<TPayload>
    implements InMessageTopicContract<TPayload>
{
    private validator: AjvValidator<TPayload>;

    public pendingAuthentication?: {
        payload: TPayload;
        uid: string;
        timeStamp: number;
    };

    protected constructor(
        schema: JSONSchemaType<TPayload>,
        public requirements: InMessageRequirements = {
            supportedAppModes: [
                AppMode.EMBEDDED_SINGLE_WIDGET,
                AppMode.EMBEDDED_DASHBOARD,
            ],
            mustBeAuthenticated: true,
        }
    ) {
        this.validator = new AjvValidator<TPayload>(schema);
    }

    public assertOkToAcceptIncoming(
        uid: string,
        payload: unknown,
        store: Store
    ): InMessageError | undefined {
        const validationResult = this.validate(payload);
        if (!validationResult.success) {
            return {
                type: 'Validation Failed',
                message: validationResult.error,
                details: validationResult,
            };
        }

        const state = store.getState();
        const appMode = selectAppMode(state);

        if (!this.requirements.supportedAppModes.find((a) => a === appMode)) {
            return {
                type: 'Invalid App Mode',
                message: `This message is not supported in application mode ${appMode}`,
            };
        }
    }

    public abstract handle(
        uid: string,
        payload: TPayload,
        store: Store,
        loggingService: LoggingService
    ): void;

    public validate(payload: unknown): ValidationResult {
        return this.validator.validate(payload);
    }

    public savePendingAuthentication(payload: TPayload, uid: string) {
        this.pendingAuthentication = {
            payload,
            uid,
            timeStamp: new Date().getTime(),
        };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyInMessageTopic = InMessageTopicContract<any>;
